import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness';

import { CLIENT_ID, getAccessToken } from '@/utils/http';
import { WikiPage, WikiSpace } from './types';

/** The realtime envelope (mirrors backend defs.Event). */
interface RealtimeEvent {
  type: string;
  board_id: string;
  actor_id?: string | null;
  client_id?: string | null;
  payload?: unknown;
}

/** Marks Yjs updates we applied FROM the network (or the initial load), so our own update/awareness
 *  observers don't echo them straight back and loop. Local edits carry a different origin, so they
 *  broadcast. */
export const REMOTE_ORIGIN = Symbol('wiki-remote');
const LOAD_ORIGIN = 'wiki-load';

/** Apply a page's persisted Yjs state into a fresh doc (origin LOAD so it isn't re-broadcast). */
export function applyLoadedDoc(ydoc: Y.Doc, base64: string | null | undefined) {
  if (base64) Y.applyUpdate(ydoc, b64ToBytes(base64), LOAD_ORIGIN);
}

/** The current full doc state, base64-encoded, for the autosave PUT. */
export function encodeDoc(ydoc: Y.Doc): string {
  return bytesToB64(Y.encodeStateAsUpdate(ydoc));
}

// ---- base64 <-> bytes (Yjs binary rides as base64 in the JSON WS frames + the autosave body) ----
function bytesToB64(u8: Uint8Array): string {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) s += String.fromCharCode(...u8.subarray(i, i + chunk));
  return btoa(s);
}
function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

const wsUrl = (room: string, actorId: string, accessToken: string | null): string => {
  const base = window.config.apiUrl.replace(/^http/, 'ws');
  const u = new URL(`${base}/ws`);
  u.searchParams.set('board_id', room);
  u.searchParams.set('client_id', CLIENT_ID);
  if (actorId) u.searchParams.set('actor_id', actorId);
  if (accessToken) u.searchParams.set('access_token', accessToken);
  return u.toString();
};

const AWARENESS_HEARTBEAT = 15000; // ms — re-broadcast our caret so peers don't age it out (30s timeout)

/**
 * Holds ONE WebSocket to a wiki page's collaborative room ("wiki-page:<id>") and turns it into
 * genuine concurrent editing + live carets by relaying Yjs document + awareness updates:
 *  - local doc edits  → broadcast as `wiki.update`; inbound `wiki.update` → applyUpdate(REMOTE)
 *  - local caret/awareness → broadcast as `wiki.awareness`; inbound → applyAwarenessUpdate(REMOTE)
 *  - on (re)connect we send our state vector (`wiki.sync`) so peers reply with the delta we lack, and
 *    we re-answer peers' sync requests with the delta THEY lack — the y-websocket handshake, minus the
 *    server (the hub is a blind relay; durability is the separate HTTP autosave of doc_state).
 * `enabled` gates the socket on the initial doc being loaded, so our first sync reflects real state.
 */
export function useWikiDoc(opts: { pageId: string | null; ydoc: Y.Doc; awareness: Awareness; selfId: string; enabled: boolean }) {
  const { pageId, ydoc, awareness, selfId, enabled } = opts;
  const [presence, setPresence] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!pageId || !enabled) return undefined;
    const room = `wiki-page:${pageId}`;
    let ws: WebSocket | null = null;
    let closedByUs = false;
    let retry = 0;
    let timer: number | undefined;

    const send = (type: string, bytes: Uint8Array) => {
      const sock = wsRef.current;
      if (sock && sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type, data: bytesToB64(bytes) }));
    };

    // Broadcast our own edits (skip anything we applied from the network / the initial load).
    const onDocUpdate = (update: Uint8Array, origin: unknown) => {
      if (origin === REMOTE_ORIGIN || origin === LOAD_ORIGIN) return;
      send('wiki.update', update);
    };
    // Broadcast our own caret/selection changes (skip states applied from peers).
    const onAwareness = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => {
      if (origin === REMOTE_ORIGIN) return;
      const changed = added.concat(updated, removed);
      if (changed.length) send('wiki.awareness', encodeAwarenessUpdate(awareness, changed));
    };
    ydoc.on('update', onDocUpdate);
    awareness.on('update', onAwareness);

    const connect = () => {
      ws = new WebSocket(wsUrl(room, selfId, getAccessToken()));
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        // Two-way (re)sync so NO edits are lost across a connect/reconnect:
        //  (1) ask peers for anything we lack (send our state vector), and
        //  (2) push our FULL state to peers so they integrate any ops we made offline / before connecting
        //      — applying a full-state update is idempotent in Yjs, so re-sending is harmless. Without (2),
        //      edits typed while disconnected (whose live broadcasts were dropped) would never propagate.
        send('wiki.sync', Y.encodeStateVector(ydoc));
        send('wiki.update', Y.encodeStateAsUpdate(ydoc));
        if (awareness.getLocalState()) send('wiki.awareness', encodeAwarenessUpdate(awareness, [ydoc.clientID]));
      };
      ws.onmessage = (e) => {
        let ev: RealtimeEvent;
        try {
          ev = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (ev.type === 'presence.sync') {
          setPresence((ev.payload as { members?: string[] })?.members ?? []);
          return;
        }
        if (ev.client_id && ev.client_id === CLIENT_ID) return; // our own echo
        const data = (ev.payload as { data?: string })?.data;
        if (!data) return;
        const bytes = b64ToBytes(data);
        if (ev.type === 'wiki.update') {
          Y.applyUpdate(ydoc, bytes, REMOTE_ORIGIN);
        } else if (ev.type === 'wiki.awareness') {
          applyAwarenessUpdate(awareness, bytes, REMOTE_ORIGIN);
        } else if (ev.type === 'wiki.sync') {
          // A peer joined and sent their state vector — reply with the delta they lack, and re-announce
          // our caret so it appears for them immediately.
          send('wiki.update', Y.encodeStateAsUpdate(ydoc, bytes));
          if (awareness.getLocalState()) send('wiki.awareness', encodeAwarenessUpdate(awareness, [ydoc.clientID]));
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedByUs) return;
        retry += 1;
        timer = window.setTimeout(connect, Math.min(1000 * 2 ** Math.min(retry, 4), 15000));
      };
      ws.onerror = () => ws?.close();
    };
    connect();

    // Keep our caret alive on peers that would otherwise time it out (awareness outdatedTime ~30s).
    const heartbeat = window.setInterval(() => {
      if (awareness.getLocalState()) send('wiki.awareness', encodeAwarenessUpdate(awareness, [ydoc.clientID]));
    }, AWARENESS_HEARTBEAT);

    return () => {
      closedByUs = true;
      if (timer) window.clearTimeout(timer);
      window.clearInterval(heartbeat);
      ydoc.off('update', onDocUpdate);
      // Tell peers our caret is gone BEFORE detaching the awareness observer + closing the socket —
      // removeAwarenessStates fires onAwareness (origin 'unmount', not REMOTE) which broadcasts the
      // removal while the socket is still open, so peers drop our caret at once (no ~30s lingering ghost).
      removeAwarenessStates(awareness, [ydoc.clientID], 'unmount');
      awareness.off('update', onAwareness);
      setPresence([]);
      setConnected(false);
      wsRef.current = null;
      ws?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, enabled]);

  return { presence, connected };
}

interface TreeHandlers {
  onUpsert: (page: WikiPage) => void;
  onDelete: (ids: string[]) => void;
  onSpaceUpsert?: (space: WikiSpace) => void;
  onSpaceDelete?: (ids: string[]) => void;
}

/**
 * Holds ONE WebSocket to the wiki AREA room ("wiki") and applies structural events (page create /
 * rename / move / delete) so every open page tree stays live, skipping this tab's own echoes.
 */
export function useWikiTree(selfId: string, h: TreeHandlers) {
  const [connected, setConnected] = useState(false);
  const hRef = useRef(h);
  hRef.current = h;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let closedByUs = false;
    let retry = 0;
    let timer: number | undefined;

    const connect = () => {
      ws = new WebSocket(wsUrl('wiki', selfId, getAccessToken()));
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
      };
      ws.onmessage = (e) => {
        let ev: RealtimeEvent;
        try {
          ev = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (ev.type === 'presence.sync') return;
        if (ev.client_id && ev.client_id === CLIENT_ID) return;
        if (ev.type === 'wiki_page.upsert') {
          const p = ev.payload as WikiPage;
          if (p && p.id) hRef.current.onUpsert(p);
        } else if (ev.type === 'wiki_page.delete') {
          const ids = ((ev.payload as { ids?: string[] })?.ids ?? []) as string[];
          if (ids.length) hRef.current.onDelete(ids);
        } else if (ev.type === 'wiki_space.upsert') {
          const s = ev.payload as WikiSpace;
          if (s && s.id) hRef.current.onSpaceUpsert?.(s);
        } else if (ev.type === 'wiki_space.delete') {
          const ids = ((ev.payload as { ids?: string[] })?.ids ?? []) as string[];
          if (ids.length) hRef.current.onSpaceDelete?.(ids);
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedByUs) return;
        retry += 1;
        timer = window.setTimeout(connect, Math.min(1000 * 2 ** Math.min(retry, 4), 15000));
      };
      ws.onerror = () => ws?.close();
    };
    connect();

    return () => {
      closedByUs = true;
      if (timer) window.clearTimeout(timer);
      setConnected(false);
      ws?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfId]);

  return { connected };
}
